import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Calendar, AlertTriangle, ArrowRight, User, Share2, Database, Users, Clock, Link2, CheckCircle2, FileText, Navigation, Mic } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import NamusImage from '@/components/NamusImage';
import { SaveCaseButton } from '@/components/SaveCaseButton';
import { formatAge } from '@/utils/ageUtils';

// Match reason badge component
const MatchReasonBadge = ({ tier, reason, type }) => {
  if (!tier || tier === undefined) return null;
  
  const config = {
    1: { 
      color: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
      icon: CheckCircle2,
      label: reason || 'Name match'
    },
    2: {
      color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300', 
      icon: FileText,
      label: reason || 'Matched in details'
    },
    3: {
      color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
      icon: Navigation,
      label: reason || 'Matched in text'
    }
  };
  
  const { color, icon: Icon, label } = config[tier] || config[3];
  
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${color}`}>
      <Icon className="w-3 h-3" />
      <span>{label}</span>
    </div>
  );
};

// Clean up messy names from data imports
// Removes leading colons, state abbreviations in parentheses, extra whitespace
const cleanName = (name) => {
  if (!name) return 'Unknown';
  return name
    .replace(/^:\s*/, '')                    // Remove leading colon and space
    .replace(/\s*\([A-Z]{2}\)\s*$/, '')      // Remove state abbreviation at end like "(AZ)"
    .replace(/\s*\([A-Za-z\s]+\)\s*$/, '')   // Remove state name at end like "(Arizona)"
    .replace(/\s+/g, ' ')                     // Normalize whitespace
    .trim();
};

export const MissingPersonCard = ({ person, index = 0 }) => {
  const isExternalCase = person.sourceType === 'ncmec' || person.sourceType === 'namus' || person.sourceType === 'namus_unidentified' ||
                         person.source === 'ncmec' || person.source === 'namus' || person.source === 'namus_unidentified';
  
  // Check if this is a canonical (merged) case
  const isCanonical = person.is_canonical === true;
  const sourceCount = person.source_count || 1;
  
  // Search match info
  const matchTier = person.match_tier;
  const matchReason = person.match_reason;
  const matchType = person.match_type;
  
  // Clean the display name
  const displayName = cleanName(person.name);
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Calculate years missing/unclaimed/unidentified
  const getYearsMissing = () => {
    // Use appropriate date based on case type
    let dateStr;
    if (person.source === 'namus_unclaimed') {
      dateStr = person.date_body_found || person.date_of_death || person.date_found;
    } else if (person.source === 'namus_unidentified') {
      dateStr = person.date_found || person.date_body_found;
    } else {
      // For missing persons - use any available date field
      dateStr = person.missing_since || person.date_missing || person.missing_date || person.last_seen_date;
    }
    
    // Don't show time missing if we have no date
    if (!dateStr) return null;
    
    try {
      const date = new Date(dateStr);
      const now = new Date();
      
      // Validate date is reasonable (not in the future, not before 1900)
      if (isNaN(date.getTime()) || date > now || date.getFullYear() < 1900) {
        return null;
      }
      
      // Calculate time difference
      const years = Math.floor((now - date) / (365.25 * 24 * 60 * 60 * 1000));
      if (years < 1) {
        const months = Math.floor((now - date) / (30 * 24 * 60 * 60 * 1000));
        if (months < 1) {
          const days = Math.floor((now - date) / (24 * 60 * 60 * 1000));
          return { value: days, unit: days === 1 ? 'day' : 'days', isRecent: true };
        }
        return { value: months, unit: months === 1 ? 'month' : 'months', isRecent: true };
      }
      return { value: years, unit: years === 1 ? 'year' : 'years', isRecent: false };
    } catch {
      return null;
    }
  };

  const yearsMissing = getYearsMissing();

  const handleQuickShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Social-share crawlers must hit `/api/c/{id}` so they reach the
    // backend SSR meta-tag route (Emergent's ingress only proxies
    // `/api/*` to FastAPI; bare `/case/{id}` returns the static SPA
    // shell with generic OG tags). The route 302-redirects humans back
    // to `/case/{id}` so the `navigator.share` UX is identical.
    // External cases (NamUs, NCMEC, etc.) keep their source_url since
    // we want the share to open the original record.
    const shareUrl = isExternalCase && person.source_url
      ? person.source_url
      : `${window.location.origin}/api/c/${person.id}`;
    const shareText = `🚨 MISSING: ${displayName}. ${person.last_seen_location || person.state ? `Last seen in ${person.last_seen_location || person.state}` : ''}. Help find them!`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: `Missing: ${displayName}`, text: shareText, url: shareUrl });
      } catch (err) {
        if (err.name !== 'AbortError') {
          navigator.clipboard.writeText(shareUrl);
          toast.success('Link copied!');
        }
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    }
  };

  const placeholderImage = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiB2aWV3Qm94PSIwIDAgNDAwIDQwMCI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSI0MDAiIGZpbGw9IiNmNWY1ZjUiLz48Y2lyY2xlIGN4PSIyMDAiIGN5PSIxNDAiIHI9IjcwIiBmaWxsPSIjZDFkNWRiIi8+PHBhdGggZD0iTTgwIDQwMHYtNDBjMC02Ni4zIDUzLjctMTIwIDEyMC0xMjBoMGM2Ni4zIDAgMTIwIDUzLjcgMTIwIDEyMHY0MCIgZmlsbD0iI2QxZDVkYiIvPjwvc3ZnPg==';

  const API = process.env.REACT_APP_BACKEND_URL;

  const isNamusUrl = (url) => {
    if (!url) return false;
    return url.includes('namus.gov');
  };

  const isFbiUrl = (url) => {
    if (!url) return false;
    return url.includes('fbi.gov');
  };

  // Convert FBI URL to proxy URL
  const getFbiProxyUrl = (url) => {
    if (!url || !isFbiUrl(url)) return null;
    return `${API}/api/external/fbi-image-proxy?url=${encodeURIComponent(url)}`;
  };

  // Convert NamUs URL to proxy URL
  const getNamusProxyUrl = (url) => {
    if (!url || !isNamusUrl(url)) return null;
    
    // Extract case type, case ID, and image ID from URL
    // Format 1: .../CaseSets/NamUs/{CaseType}/Cases/{caseId}/Images/{imageId}/...
    // Format 2: .../CaseSets/NamUs/{CaseType}/Cases/{caseId}/Images/Default/...
    const match = url.match(/CaseSets\/NamUs\/(MissingPersons|UnclaimedPersons|UnidentifiedPersons)\/Cases\/(\d+)\/Images\/(\d+|Default)/);
    if (match) {
      const [, caseType, caseId, imageId] = match;
      // Use 'Default' as imageId for default images
      return `${API}/api/external/namus-image/${caseId}/${imageId}?size=Thumbnail&case_type=${caseType}`;
    }
    
    // Fallback for older URL format - detect case type from person.source
    const fallbackMatch = url.match(/Cases\/(\d+)\/Images\/(\d+|Default)/);
    if (fallbackMatch) {
      const [, caseId, imageId] = fallbackMatch;
      let caseType = 'MissingPersons';
      if (person.source === 'namus_unclaimed') caseType = 'UnclaimedPersons';
      else if (person.source === 'namus_unidentified') caseType = 'UnidentifiedPersons';
      return `${API}/api/external/namus-image/${caseId}/${imageId}?size=Thumbnail&case_type=${caseType}`;
    }
    
    return null;
  };

  const hasWorkingPhoto = () => {
    if (person.photo_base64 || person.primary_photo_base64) return true;
    if (person.photo_url) {
      // NamUs URLs work via proxy
      if (isNamusUrl(person.photo_url)) {
        return getNamusProxyUrl(person.photo_url) !== null;
      }
      // FBI and other URLs load directly in browser
      return true;
    }
    return false;
  };

  const getImage = () => {
    // Check for base64 (external cases use photo_base64, canonical use primary_photo_base64)
    const base64 = person.photo_base64 || person.primary_photo_base64;
    if (base64) {
      return base64.startsWith('data:') 
        ? base64 
        : `data:image/jpeg;base64,${base64}`;
    }
    if (person.photo_url) {
      // Use proxy for NamUs images (they require special headers)
      if (isNamusUrl(person.photo_url)) {
        const proxyUrl = getNamusProxyUrl(person.photo_url);
        if (proxyUrl) return proxyUrl;
      }
      // Convert HTTP to HTTPS to avoid mixed content blocking
      let url = person.photo_url;
      if (url.startsWith('http://')) {
        url = url.replace('http://', 'https://');
      }
      return url;
    }
    return placeholderImage;
  };

  const showNoPhotoNotice = !hasWorkingPhoto();

  const getLocation = () => {
    if (person.last_seen_location) return person.last_seen_location;
    if (person.state) return person.state;
    return 'Location unknown';
  };

  // Determine if case is "old" (10+ years)
  const isOldCase = yearsMissing && yearsMissing.value >= 10 && yearsMissing.unit === 'years';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.4 }}
    >
      <Card 
        className={`group bg-white dark:bg-slate-900 border-0 md:border md:border-slate-200 md:dark:border-slate-800 rounded-xl overflow-hidden shadow-sm md:shadow-none hover:shadow-xl transition-all duration-300 card-hover ${
          person.is_urgent ? 'ring-2 ring-red-500/50' : ''
        }`}
        data-testid={`case-card-${person.id}`}
      >
        {/* Mobile: Entire image area is clickable */}
        <Link to={`/case/${person.id}`} className="block md:pointer-events-none">
        {/* Image with overlay info - Mobile optimized */}
        {/* Consistent 4:5 aspect ratio for all cards */}
        <div className="relative aspect-[4/5] overflow-hidden bg-slate-100 dark:bg-slate-800">
          <NamusImage
            src={getImage()}
            alt={displayName}
            className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${
              isOldCase ? 'opacity-90 saturate-[0.85]' : ''
            }`}
            fallbackSrc={placeholderImage}
            showLoader={false}
          />
          
          {/* No photo notice */}
          {showNoPhotoNotice && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
              <div className="text-center p-4">
                <User className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                  Photo not publicly available
                </p>
              </div>
            </div>
          )}
          
          {/* Multi-layer gradient overlay for better text readability */}
          {/* Top: subtle vignette for badges */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-transparent h-24 pointer-events-none" />
          {/* Bottom: stronger gradient for name/info overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />
          
          {/* Source color stripe - subtle corner indicator on mobile */}
          {isExternalCase && (
            <div className={`absolute top-0 left-0 w-1 h-12 md:hidden ${
              person.source === 'namus' 
                ? 'bg-green-500'
                : person.source === 'namus_unidentified'
                  ? 'bg-purple-500'
                  : person.source === 'charley'
                    ? 'bg-amber-500'
                    : 'bg-trust-blue-500'
            }`} />
          )}
          
          {/* Top badges - Urgent & Status - Smaller on mobile */}
          <div className="absolute top-2 md:top-3 left-2 md:left-3 flex flex-wrap gap-1.5 md:gap-2">
            {person.is_urgent && (
              <Badge 
                className="bg-red-500 text-white border-0 urgent-badge flex items-center gap-0.5 md:gap-1 text-[9px] md:text-xs px-1.5 md:px-2 py-0.5 animate-pulse"
                data-testid="urgent-badge"
              >
                <AlertTriangle className="w-2.5 h-2.5 md:w-3 md:h-3" />
                <span className="hidden md:inline">URGENT</span>
                <span className="md:hidden">!</span>
              </Badge>
            )}
            {person.status === 'found' && (
              <Badge className="bg-green-500 text-white border-0 text-[9px] md:text-xs px-1.5 md:px-2 py-0.5">
                FOUND
              </Badge>
            )}
            {isCanonical && sourceCount > 1 && (
              <Badge 
                className="bg-blue-500 text-white border-0 flex items-center gap-0.5 text-[9px] md:text-xs px-1.5 md:px-2 py-0.5"
                data-testid="merged-sources-badge"
              >
                <Link2 className="w-2.5 h-2.5 md:w-3 md:h-3" />
                <span className="hidden md:inline">{sourceCount} Sources</span>
                <span className="md:hidden">{sourceCount}</span>
              </Badge>
            )}
            {person.has_potential_duplicate && (
              <Badge className="bg-purple-500 text-white border-0 flex items-center gap-0.5 text-[9px] md:text-xs px-1.5 md:px-2 py-0.5">
                <Users className="w-2.5 h-2.5 md:w-3 md:h-3" />
                <span className="hidden md:inline">MATCH</span>
              </Badge>
            )}
            {Array.isArray(person.flags)
              && person.flags.includes('PODCAST_COVERAGE_AVAILABLE')
              && (() => {
                // Count distinct podcasts covering this case. The API ships
                // `podcast_coverage_episodes` alongside the flag; we dedupe
                // by `podcast_key` here so a multi-part series from a single
                // show still counts as one podcast.
                const eps = Array.isArray(person.podcast_coverage_episodes)
                  ? person.podcast_coverage_episodes
                  : [];
                const distinct = new Set(
                  eps.map((e) => e?.podcast_key).filter(Boolean)
                ).size;
                // Fall back to "1 podcast" if the array isn't hydrated for
                // some reason (legacy single-Fall-Line coverage, partial
                // payload from a paginated endpoint, etc.)
                const count = distinct || 1;
                return (
                  <Badge
                    className="bg-purple-100 text-purple-800 border border-purple-300
                               flex items-center gap-0.5 md:gap-1 text-[9px] md:text-xs
                               px-1.5 md:px-2 py-0.5"
                    data-testid="podcast-coverage-badge"
                    title={`Covered in ${count} podcast${count === 1 ? '' : 's'}`}>
                    <Mic className="w-2.5 h-2.5 md:w-3 md:h-3" />
                    <span className="hidden md:inline">
                      Covered in {count} podcast{count === 1 ? '' : 's'}
                    </span>
                    <span className="md:hidden">{count}</span>
                  </Badge>
                );
              })()}
          </div>

          {/* Quick Share Button - Hidden on mobile (use sticky bar instead), show on desktop hover */}
          <button
            onClick={handleQuickShare}
            className="absolute top-2 md:top-3 right-2 md:right-3 w-7 h-7 md:w-8 md:h-8 bg-white/90 dark:bg-slate-900/90 rounded-full flex items-center justify-center shadow-md hidden md:flex md:opacity-0 md:group-hover:opacity-100 transition-opacity hover:bg-white dark:hover:bg-slate-800"
            data-testid={`quick-share-${person.id}`}
          >
            <Share2 className="w-3.5 h-3.5 md:w-4 md:h-4 text-trust-blue-500" />
          </button>

          {/* MOBILE: Key info overlay on image */}
          <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4">
            {/* Name - Bold & prominent - LARGER on mobile */}
            <h3 className="font-heading font-bold text-xl md:text-xl text-white line-clamp-1">
              {displayName}
            </h3>
            
            {/* Years missing - Moved directly under name as emotional anchor */}
            {yearsMissing && (
              <p className={`text-sm font-medium mt-0.5 mb-1.5 ${
                person.is_urgent || yearsMissing.isRecent
                  ? 'text-safety-orange-300'
                  : 'text-white/90'
              }`}>
                {/* Handle "0 days" case - show "Reported Today" instead */}
                {yearsMissing.value === 0 && yearsMissing.unit === 'days'
                  ? (person.source === 'namus_unidentified' 
                      ? 'Found Today'
                      : person.source === 'namus_unclaimed'
                        ? 'Reported Today'
                        : 'Reported Today')
                  : person.source === 'namus_unidentified' 
                    ? `Unidentified ${yearsMissing.value} ${yearsMissing.unit}`
                    : person.source === 'namus_unclaimed'
                      ? `Unclaimed ${yearsMissing.value} ${yearsMissing.unit}`
                      : `Missing ${yearsMissing.value} ${yearsMissing.unit}`}
              </p>
            )}
            
            {/* Age / Sex - Quick scan info - reduced opacity */}
            {(person.age || person.age_min || person.age_max || person.age_range || person.gender) && (
              <p className="text-white/70 text-xs mb-1">
                {(person.age || person.age_min || person.age_max || person.age_range) && formatAge(person, { compact: true, showEstimated: false })}
                {(person.age || person.age_min || person.age_max || person.age_range) && person.gender && ' • '}
                {person.gender}
              </p>
            )}
            
            {/* Location - Always show with pin icon */}
            <p className="text-white/70 text-xs flex items-center gap-1.5">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="line-clamp-1">{getLocation()}</span>
            </p>
          </div>
        </div>
        </Link>

        {/* Below image section - streamlined for mobile */}
        <CardContent className="p-3 md:p-4">
          {/* Last seen date - Subtle secondary info */}
          {(() => {
            let dateToShow = null;
            let label = 'Last seen';
            
            if (person.source === 'namus_unclaimed') {
              dateToShow = person.date_body_found || person.date_found || person.date_of_death;
              label = 'Body found';
            } else if (person.source === 'namus_unidentified') {
              dateToShow = person.date_body_found || person.date_found || person.date_of_death;
              label = 'Found';
            } else {
              // For missing persons - use any available date field
              dateToShow = person.missing_since || person.date_missing || person.missing_date || person.last_seen_date;
            }
            
            // Validate the date is parseable and reasonable
            if (dateToShow) {
              const parsed = new Date(dateToShow);
              if (isNaN(parsed.getTime()) || parsed.getFullYear() < 1900 || parsed > new Date()) {
                dateToShow = null;
              }
            }
            
            return dateToShow ? (
              <p className="text-muted-foreground/70 text-xs flex items-center gap-1.5 mb-2">
                <Calendar className="w-3 h-3" />
                {`${label} ${formatDate(dateToShow)}`}
              </p>
            ) : null;
          })()}
          
          {/* Match reason badge - shows why this result matched */}
          {matchTier && (
            <div className="mb-2">
              <MatchReasonBadge tier={matchTier} reason={matchReason} type={matchType} />
            </div>
          )}
          
          {/* Bottom row: Source indicator + View Details */}
          <div className="flex items-center justify-between gap-2">
            {/* Source - Dot on mobile, Badge on desktop */}
            <div className="flex items-center gap-2">
              {isCanonical ? (
                <>
                  {/* Canonical/Merged: Show multi-source indicator */}
                  <div className="md:hidden w-2.5 h-2.5 rounded-full shrink-0 bg-blue-500" title="Merged Sources" />
                  <Badge 
                    variant="secondary"
                    className="hidden md:inline-flex text-[10px] px-2 py-0.5 shrink-0 bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300 max-w-[100px] truncate"
                  >
                    <Link2 className="w-3 h-3 mr-1 flex-shrink-0" />
                    <span className="truncate">{sourceCount} sources</span>
                  </Badge>
                </>
              ) : isExternalCase && (
                <>
                  {/* Mobile: Just a colored dot */}
                  <div className={`md:hidden w-2.5 h-2.5 rounded-full shrink-0 ${
                    person.source === 'namus' 
                      ? 'bg-green-500'
                      : person.source === 'namus_unidentified'
                        ? 'bg-purple-500'
                        : person.source === 'charley'
                          ? 'bg-amber-500'
                          : 'bg-trust-blue-500'
                  }`} title={person.source?.toUpperCase()} />
                  
                  {/* Desktop: Full badge */}
                  <Badge 
                    variant="secondary"
                    className={`hidden md:inline-flex text-[10px] px-2 py-0.5 shrink-0 ${
                      person.source === 'namus' 
                        ? 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300'
                        : person.source === 'namus_unidentified'
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300'
                          : person.source === 'charley'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
                            : 'bg-trust-blue-100 text-trust-blue-700 dark:bg-trust-blue-950 dark:text-trust-blue-300'
                    }`}
                  >
                    {person.source?.toUpperCase().replace('_', ' ')}
                  </Badge>
                </>
              )}
            </div>
            
            {/* View Case Button - Visible on desktop, hidden on mobile (whole card is clickable on mobile) */}
            <div className="hidden md:flex items-center gap-1">
              <SaveCaseButton 
                caseId={person.id}
                caseData={{
                  case_type: person.case_type || person.sourceType,
                  source: person.source,
                  name: person.name || person.firstName && person.lastName ? `${person.firstName} ${person.lastName}` : 'Unknown',
                  thumbnail_url: person.photos?.[0] || person.image_url
                }}
                variant="icon"
              />
              <Link to={`/case/${person.id}`} data-testid={`view-case-btn-${person.id}`}>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-xs h-7 px-3 text-trust-blue-600 hover:text-trust-blue-700 hover:bg-trust-blue-50 dark:text-trust-blue-400 dark:hover:text-trust-blue-300 dark:hover:bg-trust-blue-950/50 rounded-full"
                >
                  View Case
                  <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};